import React, { useEffect, useRef } from 'react';
import type { EntityHighlight } from '../../types/resource';

interface PlainViewerProps {
  content: string;
  /** true = annotated HTML file; false = raw text file, rendered as text. */
  isHtml?: boolean;
  /** Entity categories highlighted wholesale (Analytics group rows). */
  highlightCategories?: string[];
  /** A single entity value highlighted on its own (Analytics child rows). */
  highlightEntity?: EntityHighlight | null;
}

const style: React.CSSProperties = {
  whiteSpace: 'pre-line',
  fontFamily: '"Times New Roman", Times, serif',
  padding: '.5in',
};

// Marks elements this component styled, so the next pass can find and clear them
// without disturbing classes that came down in the annotated HTML itself.
const ENTITY_MARK = 'iio-entity-highlight';

export default function PlainViewer({
  content,
  isHtml = false,
  highlightCategories = [],
  highlightEntity = null,
}: PlainViewerProps) {
  const ref = useRef<HTMLDivElement>(null);

  // The annotated HTML wraps entities in tags named after the entity type (<person>,
  // <location>, …): class the tag whose text contains the value. Categories instead go on
  // the container, which the `.highlight-person person { … }` rules key off.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.querySelectorAll(`.${ENTITY_MARK}`).forEach((node) => {
      node.classList.forEach((c) => { if (c.startsWith('highlight-')) node.classList.remove(c); });
      node.classList.remove(ENTITY_MARK);
    });
    if (!isHtml || !highlightEntity) return;
    const { type, value } = highlightEntity;
    let matches: Element[];
    try {
      matches = Array.from(el.querySelectorAll(type));
    } catch {
      return; // entity type isn't a usable selector
    }
    matches
      .filter((node) => node.textContent?.includes(value))
      .forEach((node) => node.classList.add(`highlight-${type}`, ENTITY_MARK));
  }, [isHtml, highlightEntity, content]);

  const className = ['iio-plain-viewer', 'annotated-text']
    .concat(highlightCategories.map((c) => `highlight-${c}`))
    .join(' ');

  if (!isHtml) {
    return <div ref={ref} className={className} style={style}>{content}</div>;
  }
  return (
    <div
      ref={ref}
      className={className}
      style={style}
      // Server-rendered HTML from trusted internal API
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
}
